import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ErrorMessage from 'components/application/ErrorMessage';
import SidePanel from 'components/application/SidePanel';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import FormFooter from 'components/sidepanels/FormFooter';
import AttachmentsField from 'components/training/attachments/components/AttachmentsField';
import TrainingFormFields from 'components/training/TrainingFormFields';

import { sendAnalytics } from 'components/helpers/analytics';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function TrainingSidePanel({
  closeSidePanel,
  createTraining,
  currentTraining,
  domainCourse,
  includedAttachments,
  includedCoverImages,
  onCancelTraining,
  onInputChange,
  onTrainingCalendarClose,
  onTrainingDateChange,
  personnelId,
  removeErrorStyling,
  requestError,
  sidePanelContext,
  sidePanelIsOpen,
  submitDisabled,
  updateTraining,
}) {
  const { user: currentUser } = useCurrentActor();

  const [scrolledAmount, setScrolledAmount] = useState(0);

  const { t: i18nTCommon } = useTranslation('common');

  const courseAttributes = currentTraining.course?.attributes || {};

  const currentTrainingAttachments = useMemo(
    () =>
      includedAttachments.filter((a) =>
        currentTraining.attachmentIds.includes(Number(a.id)),
      ),
    [includedAttachments, currentTraining.attachmentIds],
  );

  const onBackdropClick = () =>
    !['new', 'edit'].includes(sidePanelContext) && closeSidePanel();

  return (
    <SidePanel
      bodyContent={
        <div className='popup__body-form'>
          <ErrorMessage
            isFallback={requestError.isFallback}
            validationErrors={requestError.validationErrors}
            wrapperClassName='form-container'
          />
          <div className='form-container'>
            <TrainingFormFields
              currentTraining={currentTraining}
              domainCourse={domainCourse}
              includedAttachments={includedAttachments}
              includedCoverImages={includedCoverImages}
              onInputChange={onInputChange}
              onTrainingCalendarClose={onTrainingCalendarClose}
              onTrainingDateChange={onTrainingDateChange}
              personnelId={personnelId}
              removeErrorStyling={removeErrorStyling}
              requestError={requestError}
              sidePanelIsOpen={sidePanelIsOpen}
              submitDisabled={submitDisabled}
            />
            <AttachmentsField
              isVisible={sidePanelIsOpen}
              label={i18nTCommon('shared.uploadEvidence')}
              maxUploads={25}
              name='attachmentIds'
              onChange={onInputChange}
              onUploadSuccess={() =>
                sendAnalytics('Training evidence uploaded', {
                  currentUser,
                  personnelId,
                })
              }
              seedAttachments={{
                data: currentTrainingAttachments,
                included: includedCoverImages,
              }}
              tooltip={i18nTCommon('shared.egCertificatesOrCards')}
              value={currentTraining.attachmentIds}
            />
          </div>
        </div>
      }
      closeCallback={closeSidePanel}
      color={sidePanelContext === 'show' ? 'secondary' : 'tertiary'}
      contentContext={sidePanelContext}
      displayClose={false}
      footerContent={
        <FormFooter
          onCancel={onCancelTraining}
          onSubmit={
            sidePanelContext === 'new' ? createTraining : updateTraining
          }
          submitButtonDisabled={submitDisabled}
          text={
            sidePanelContext === 'new' ?
              i18nTCommon('training.recordTraining')
            : i18nTCommon('shared.saveChanges')
          }
        />
      }
      headerContent={
        <div className='popup__title popup__title--tertiary-split'>
          <h1 className='tw-mb-2 tw-text-s tw-font-medium tw-tracking-tight'>
            {sidePanelContext === 'new' ?
              i18nTCommon('training.recordNewTraining')
            : i18nTCommon('training.editTraining')}
          </h1>
          <h2 className='truncated-text-container--wrapped truncated-text-container--five-lines tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
            {courseAttributes.name}
          </h2>
        </div>
      }
      isOpen={sidePanelIsOpen}
      onBackdropClick={onBackdropClick}
      scrolledAmount={scrolledAmount}
      setScrolledAmount={setScrolledAmount}
      submitDisabled={submitDisabled}
    />
  );
}

TrainingSidePanel.propTypes = {
  domainCourse: resourceShape('course'),
  currentTraining: PropTypes.object.isRequired,
  sidePanelContext: PropTypes.string.isRequired,
  sidePanelIsOpen: PropTypes.bool.isRequired,
  closeSidePanel: PropTypes.func.isRequired,
  requestError: PropTypes.object.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  includedAttachments: PropTypes.array.isRequired,
  includedCoverImages: PropTypes.array.isRequired,
  submitDisabled: PropTypes.bool.isRequired,
  personnelId: PropTypes.string.isRequired,
  onTrainingDateChange: PropTypes.func.isRequired,
  onTrainingCalendarClose: PropTypes.func.isRequired,
  createTraining: PropTypes.func.isRequired,
  updateTraining: PropTypes.func.isRequired,
  onCancelTraining: PropTypes.func.isRequired,
};

TrainingSidePanel.defaultProps = {
  sidePanelContext: 'new',
};
